/**
 * Versandübersicht der Bewertungsbögen — ohne React, damit die Regeln ohne
 * Rendering prüfbar sind.
 *
 * **Was hier zusammengeführt wird.** Die Tabelle listete je *Antwort* eine
 * Zeile. Weil ein erneuter Versand eine offene Einladung wiederverwendet, eine
 * bereits abgegebene aber nicht, erschien derselbe Mandant je nach Zustand
 * einmal oder zweimal — ohne dass die Maske das erklärte. Genau darüber
 * stolperte die QS (qs/dino-qs#9, TF1, Vorgang 4075 gegen 4028).
 *
 * Die Regel steckt in `sendRatingForLegalTransaction` (ratingform.py):
 *
 *   * Status `pending`/`sent`/`opened` ohne Abgabe -> **dieselbe** Einladung
 *     wird erneut verschickt, der Link bleibt gültig.
 *   * bereits abgegeben (oder fehlgeschlagen) -> **neue** Einladung mit neuem
 *     Link; der Mandant kann ein zweites Mal bewerten und steht dann zweimal
 *     in der Liste.
 *
 * Einen zeitlichen Mindestabstand gibt es nicht — den hatte die QS vermutet.
 * Entscheidend ist allein, ob schon abgegeben wurde.
 */

export interface IResponseLike {
  idRatingResponse: number;
  idLegalClient: number;
  FirstName?: string | null;
  LastName?: string | null;
  Email?: string | null;
  Status?: string | null;
  SentAt?: string | null;
  SubmittedAt?: string | null;
  Created_at?: string | null;
  OverallScore?: number | null;
}

export interface IClientGroup<T extends IResponseLike> {
  idLegalClient: number;
  name: string;
  email: string | null;
  /** Jüngste Einladung — die, um die es gerade geht. */
  latest: T;
  /** Ältere Einladungen desselben Mandanten, jüngste zuerst. */
  older: T[];
}

/** Ein Zustand, in dem eine Einladung noch beantwortet werden kann. */
const OPEN_STATES = ['pending', 'sent', 'opened'];

export const isOpenInvitation = (response: IResponseLike): boolean =>
  !response.SubmittedAt && OPEN_STATES.includes(response.Status ?? 'pending');

export const clientName = (response: IResponseLike): string =>
  [response.FirstName, response.LastName].filter(Boolean).join(' ') ||
  response.Email ||
  `#${response.idLegalClient}`;

/** Sortierschlüssel: jüngste zuerst. `Created_at` ist der verlässlichere
 *  Wert — `SentAt` wird beim Wiederverwenden überschrieben. */
const newestFirst = (a: IResponseLike, b: IResponseLike): number =>
  (b.Created_at ?? '').localeCompare(a.Created_at ?? '') ||
  b.idRatingResponse - a.idRatingResponse;

/** Eine Zeile pro Mandant, ältere Einladungen darunter. */
export const groupResponsesByClient = <T extends IResponseLike>(
  responses: T[] | null | undefined
): IClientGroup<T>[] => {
  const byClient = new Map<number, T[]>();
  for (const response of responses ?? []) {
    const list = byClient.get(response.idLegalClient) ?? [];
    list.push(response);
    byClient.set(response.idLegalClient, list);
  }

  const groups: IClientGroup<T>[] = [];
  byClient.forEach(list => {
    const sorted = [...list].sort(newestFirst);
    groups.push({
      idLegalClient: sorted[0].idLegalClient,
      name: clientName(sorted[0]),
      email: sorted[0].Email ?? null,
      latest: sorted[0],
      older: sorted.slice(1),
    });
  });

  return groups.sort((a, b) => a.name.localeCompare(b.name, 'de'));
};

export interface IDispatchSummary {
  /** Wurde überhaupt schon etwas versendet? */
  hasAny: boolean;
  /** Mandanten, deren Einladung noch offen ist — sie bekommen sie erneut. */
  reused: number;
  /** Mandanten, die bereits bewertet haben — sie bekommen einen neuen Bogen. */
  submitted: number;
  /** Fehlgeschlagene Versände; sie werden nicht wiederverwendet. */
  failed: number;
}

export const dispatchSummary = (
  responses: IResponseLike[] | null | undefined
): IDispatchSummary => {
  const groups = groupResponsesByClient(responses);
  let reused = 0;
  let submitted = 0;
  let failed = 0;

  for (const group of groups) {
    if (group.latest.SubmittedAt) submitted += 1;
    else if (group.latest.Status === 'failed') failed += 1;
    else if (isOpenInvitation(group.latest)) reused += 1;
  }

  return { hasAny: groups.length > 0, reused, submitted, failed };
};

/**
 * Text der Rückfrage vor einem erneuten Versand, oder `null`, wenn noch nie
 * versendet wurde (dann braucht es keine Rückfrage).
 *
 * Der Text nennt die Folgen einzeln — dass ein bereits bewertender Mandant
 * einen **neuen** Bogen bekommt und danach zweimal in der Liste steht, war der
 * eigentliche Stolperstein.
 */
export const dispatchConfirmationText = (
  responses: IResponseLike[] | null | undefined
): string | null => {
  const summary = dispatchSummary(responses);
  if (!summary.hasAny) return null;

  const parts: string[] = [];
  if (summary.reused > 0) {
    parts.push(
      summary.reused === 1
        ? '1 Mandant erhält seine bisherige Einladung noch einmal — der bereits verschickte Link bleibt gültig.'
        : `${summary.reused} Mandanten erhalten ihre bisherige Einladung noch einmal — die bereits verschickten Links bleiben gültig.`
    );
  }
  if (summary.submitted > 0) {
    parts.push(
      summary.submitted === 1
        ? '1 Mandant hat bereits bewertet und erhält einen neuen Bogen. Die bisherige Bewertung bleibt erhalten; der Mandant erscheint danach zweimal in der Liste.'
        : `${summary.submitted} Mandanten haben bereits bewertet und erhalten einen neuen Bogen. Die bisherigen Bewertungen bleiben erhalten; diese Mandanten erscheinen danach zweimal in der Liste.`
    );
  }
  if (summary.failed > 0) {
    parts.push(
      `Für ${summary.failed} Mandant(en) ist der Versand zuvor fehlgeschlagen — dafür wird ein neuer Bogen angelegt.`
    );
  }

  parts.push('Möchten Sie den Bewertungsbogen erneut versenden?');
  return parts.join(' ');
};
